"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState, useTransition } from "react";
import { PostActionSheet } from "@/components/profile/post-action-sheet";
import UpgradeModal from "@/components/ui/upgrade-modal";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import type { ProfileFieldId } from "@/lib/profile/field-config";
import {
	GUEST_SAVE_PROMPT_AT,
	GUEST_SAVE_PROMPT_EVENT,
	isGuestJobSaved,
	toggleGuestSavedJob,
} from "@/lib/saved-jobs";
import { toggleSavedJob } from "@/server/actions/jobs";

export type SaveJobButtonProps = {
	jobId: string;
	initialIsSaved: boolean;
	isAuthenticated: boolean;
	/**
	 * Kept for caller compatibility (still threaded down from every feed
	 * surface's server-computed `UserPlan`) but no longer read here — every
	 * `UserPlan` variant except `guest` has `canSaveJobs: true`
	 * (`src/lib/entitlements.ts`), and guests save on-device (localStorage)
	 * without ever reaching the server action. The `!canSaveJobs` upgrade-modal branch this prop used to gate
	 * was therefore structurally unreachable for any real click — confirmed
	 * zero `upgrade_modal_*` fires ever (telemetry-gap-remediation
	 * 2026-07-21) — and was removed. Saving a job has no plan-based limit
	 * today; the app's real paywall is the daily-apply cap
	 * (`UserPlan.dailyApplies`), surfaced elsewhere.
	 */
	canSaveJobs: boolean;
	locale?: string;
	t?: Dictionary;
	className?: string;
	categorySlugs?: string[];
	/** Render a visible text label beside the icon. Off by default (icon-only)
	 *  for tight contexts; on for the feed card where the bookmark went
	 *  unnoticed (job_saved fired for ~3 users in 28 days). */
	showLabel?: boolean;
};

export function SaveJobButton({
	jobId,
	initialIsSaved,
	isAuthenticated,
	locale,
	t,
	className,
	categorySlugs,
	showLabel = false,
}: SaveJobButtonProps) {
	const { trackSaveToggle } = useAnalytics();
	const [isPending, startTransition] = useTransition();
	const [isSaved, setIsSaved] = useState(initialIsSaved);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	const [postSaveField, setPostSaveField] = useState<ProfileFieldId | null>(
		null,
	);
	const posthog = usePostHog();
	const [deviceToast, setDeviceToast] = useState(false);
	const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Guests: the server can't know device saves (pages stay cacheable, no
	// cookie), so hydrate the filled state from localStorage after mount.
	useEffect(() => {
		if (!isAuthenticated && isGuestJobSaved(jobId)) setIsSaved(true);
	}, [isAuthenticated, jobId]);

	useEffect(
		() => () => {
			if (toastTimer.current) clearTimeout(toastTimer.current);
		},
		[],
	);

	const handleGuestSave = () => {
		// Never throws: storage failures come back as `persisted: false` and the
		// heart still flips for this page view.
		const result = toggleGuestSavedJob(jobId);
		setIsSaved(result.isSaved);
		if (!result.isSaved || !result.persisted) return;

		posthog?.capture("guest_job_saved", {
			count: result.count,
			job_id: jobId,
		});
		setDeviceToast(true);
		if (toastTimer.current) clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setDeviceToast(false), 2500);
		// Third device save: ask (dismissibly) to keep them via signup.
		if (result.count === GUEST_SAVE_PROMPT_AT) {
			window.dispatchEvent(new CustomEvent(GUEST_SAVE_PROMPT_EVENT));
		}
	};

	const handleSave = () => {
		if (!isAuthenticated) {
			handleGuestSave();
			return;
		}

		// Optimistic UI update
		const nextSavedState = !isSaved;
		setIsSaved(nextSavedState);

		// Track the action
		trackSaveToggle(jobId, nextSavedState, {
			origin_surface: "feed",
			category_slugs: categorySlugs ?? [],
		});

		startTransition(async () => {
			try {
				const result = await toggleSavedJob(jobId);
				if ("error" in result) {
					if (result.error === "upgrade_required") {
						setIsSaved(false);
						setShowUpgradeModal(true);
					}
					return;
				}
				setIsSaved(result.isSaved);
				if (result.nudge) {
					setPostSaveField(result.nudge.field);
				}
			} catch (error) {
				// Revert on failure
				setIsSaved((prev) => !prev);
				console.error("Failed to toggle saved job:", error);
			}
		});
	};

	const stateClassName = isSaved
		? "text-primary hover:bg-primary/10"
		: "text-muted-foreground hover:bg-muted hover:text-foreground";
	// 16px icon + p-3.5 (14px) padding on each side = 44px hit area (WCAG
	// 2.5.8 minimum) — the pre-PR-4 default (`p-1.5`, 28px) undershot it.
	const sizeClassName = className || "ml-2 p-3.5";

	return (
		<>
			<button
				type="button"
				onClick={handleSave}
				disabled={isPending}
				aria-pressed={isSaved}
				aria-label={
					isSaved ? (t?.unsaveJob ?? "Unsave job") : (t?.saveJob ?? "Save job")
				}
				className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed ${stateClassName} ${sizeClassName}`}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill={isSaved ? "currentColor" : "none"}
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					role="img"
					aria-hidden="true"
				>
					<path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
				</svg>
				{showLabel && (
					<span className="text-sm font-medium">
						{isSaved ? (t?.savedLabel ?? "Saved") : (t?.saveLabel ?? "Save")}
					</span>
				)}
			</button>

			<UpgradeModal
				isOpen={showUpgradeModal}
				onClose={() => setShowUpgradeModal(false)}
				t={t || {}}
			/>

			{deviceToast ? (
				<p
					role="status"
					data-testid="guest-save-toast"
					className="fixed inset-x-4 top-4 z-50 mx-auto w-fit max-w-sm rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-card-foreground shadow-lg"
				>
					{locale === "id"
						? "Tersimpan di perangkat ini"
						: "Saved on this device"}
				</p>
			) : null}

			{postSaveField ? (
				<PostActionSheet
					field={postSaveField}
					surface="post_save_job"
					eyebrow={t?.jobSavedEyebrow ?? "Job saved · faster matches"}
					t={t}
					onClose={() => setPostSaveField(null)}
				/>
			) : null}
		</>
	);
}
